"use client";

import { Division } from "@gettactic/api";
import { useLoadingToast } from "@gettactic/helpers/src/hooks/toasts/useLoadingToast";
import { useInvalidateDivisions } from "@gettactic/hooks";
import {
  MapCanvasValue,
  MapSaveOptions
} from "@gettactic/map-builder/src/components/map/contexts/MapCanvasContext";
import { MapBuilder } from "@gettactic/map-builder/src/MapBuilder";
import { MapEditor as SvgMapEditor } from "@gettactic/map-editor";
import React from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api/api";
import { useSvgMapEditorEnabled } from "@/lib/hooks/useSvgMapEditorEnabled";

export interface DivisionMapEditorProps {
  division: Division;
  onClose: () => void;
  /** Fired after a successful save with the saved map JSON — lets hosts
   *  refresh their copy of the division (e.g. the campus map view). */
  onSaved?: (mapData: string) => void;
  /** Custom toolbar title node (e.g. map switchers). */
  headerSlot?: React.ReactNode;
}

/**
 * Full-screen map editor for a division's own map (e.g. a campus overview
 * that drills down into office maps via linked zones). Saves versioned map
 * JSON through /divisions/:id/maps — the same draft / activate-now /
 * schedule contract as floors — but with no resource lifecycle (division
 * maps have no bookable spots).
 */
export const DivisionMapEditor: React.FC<DivisionMapEditorProps> = ({
  division,
  onClose,
  onSaved,
  headerSlot
}) => {
  const invalidateDivisions = useInvalidateDivisions();
  // Same props either way; the SVG editor is flag-gated per user.
  const EditorComponent = useSvgMapEditorEnabled() ? SvgMapEditor : MapBuilder;
  const toast = useLoadingToast({
    loadingMessage: "Saving division map..."
  });

  const mapValue = React.useMemo(() => {
    if (!division.map_data) return undefined;
    try {
      return JSON.parse(division.map_data) as MapCanvasValue;
    } catch {
      return undefined;
    }
  }, [division.map_data]);

  const handleSave = async (
    data: MapCanvasValue,
    saveOptions: MapSaveOptions
  ) => {
    try {
      const body: { map_data: string; map_active_from?: string } = {
        map_data: JSON.stringify(data)
      };
      if (saveOptions.mode === "activate-now") {
        body.map_active_from = new Date().toISOString();
      } else if (saveOptions.mode === "schedule" && saveOptions.activeFrom) {
        body.map_active_from = saveOptions.activeFrom;
      }

      // Versioned save through /divisions/:id/maps — same contract as the
      // floor flow on /resources/:id/maps (draft / activate-now / schedule).
      const response = await api.client.divisions.mapPost(division.id, body);
      const savedMapData = response.result?.map_data ?? body.map_data;
      await invalidateDivisions();
      onSaved?.(savedMapData);
      toast.success("Division map saved");
    } catch {
      toast.error("The division map could not be saved");
    }
  };

  // Portaled to body so the overlay escapes whatever stacking context the
  // host renders in (toolbars, cards) and covers body-portaled map chrome
  // (minimap, zoom controls, legend). z-overlay: above that chrome but
  // below dialog overlays (z-drawer) so the editor's own dialogs — Save,
  // exit confirm — render on top of it.
  return createPortal(
    <div className="fixed inset-0 z-overlay bg-background-primary">
      <EditorComponent
        onSave={handleSave}
        value={mapValue}
        resources={[]}
        resourceId={division.id}
        title={division.name}
        titlePrefix="Division"
        headerSlot={headerSlot}
        setEditingResources={() => {}}
        onExit={onClose}
        fetchMapVersions={async (divisionId) => {
          const response = await api.client.divisions.mapsGet(divisionId);
          return response.result?.elements || [];
        }}
        activateMapVersion={async (divisionId, mapVersionId) => {
          await api.client.divisions.mapActivate(divisionId, mapVersionId);
          await invalidateDivisions();
        }}
      />
    </div>,
    document.body
  );
};
